import type { KeyResult, Objective, ObjectiveStatusT } from '@portfolio-pm/shared';
import { X } from 'lucide-react';
import { useMemo, useState } from 'react';
import { ProgressBar, StatusBadge } from './okr-ui.tsx';

interface OkrStrategyMapProps {
  rootObjectives: Objective[];
  objectivesById: Map<string, Objective>;
  childrenByParent: Map<string, Objective[]>;
  keyResultsByObjective: Map<string, KeyResult[]>;
  getObjectiveProgress: (id: string) => number;
  getKrProgress: (id: string) => number;
}

const CARD_W = 260;
const CARD_H = 150;
const COL_GAP = 60;
const ROW_GAP = 24;
const CANVAS_PAD = 24;
const COL_STEP = CARD_W + COL_GAP;

interface NodePos {
  x: number;
  y: number;
  id: string;
}

function computeLayout(
  rootObjectives: Objective[],
  childrenByParent: Map<string, Objective[]>,
): Map<string, { x: number; y: number }> {
  const pos = new Map<string, { x: number; y: number }>();
  let globalY = CANVAS_PAD;

  const place = (objId: string, depth: number, visited: Set<string>): number => {
    if (visited.has(objId)) return globalY;
    const nextVisited = new Set(visited);
    nextVisited.add(objId);

    const children = (childrenByParent.get(objId) ?? []).filter((c) => !nextVisited.has(c.id));

    if (children.length === 0) {
      const y = globalY;
      globalY += CARD_H + ROW_GAP;
      pos.set(objId, { x: CANVAS_PAD + depth * COL_STEP, y });
      return y + CARD_H / 2;
    }

    const childCenters = children.map((c) => place(c.id, depth + 1, nextVisited));
    const top = childCenters[0] ?? globalY;
    const bottom = childCenters[childCenters.length - 1] ?? globalY;
    const centerY = (top + bottom) / 2 - CARD_H / 2;
    pos.set(objId, { x: CANVAS_PAD + depth * COL_STEP, y: centerY });
    return centerY + CARD_H / 2;
  };

  for (const root of rootObjectives) {
    place(root.id, 0, new Set());
  }

  return pos;
}

function buildConnectors(
  positions: Map<string, { x: number; y: number }>,
  childrenByParent: Map<string, Objective[]>,
): { path: string; key: string }[] {
  const paths: { path: string; key: string }[] = [];

  for (const [parentId, children] of childrenByParent) {
    const parentPos = positions.get(parentId);
    if (!parentPos) continue;

    const px = parentPos.x + CARD_W;
    const py = parentPos.y + CARD_H / 2;

    for (const child of children) {
      const childPos = positions.get(child.id);
      if (!childPos) continue;
      const cx = childPos.x;
      const cy = childPos.y + CARD_H / 2;
      const mx = (px + cx) / 2;
      paths.push({
        key: `${parentId}-${child.id}`,
        path: `M ${px} ${py} C ${mx} ${py}, ${mx} ${cy}, ${cx} ${cy}`,
      });
    }
  }

  return paths;
}

const statusRingClass: Record<ObjectiveStatusT, string> = {
  on_track: 'ring-2 ring-[color:var(--app-success)]',
  at_risk: 'ring-2 ring-[color:var(--app-warning)]',
  off_track: 'ring-2 ring-[color:var(--app-danger)]',
};

const labelClass = 'eyebrow-label';
const innerCardClass = 'surface-panel-muted';

export function OkrStrategyMap({
  rootObjectives,
  objectivesById,
  childrenByParent,
  keyResultsByObjective,
  getObjectiveProgress,
  getKrProgress,
}: OkrStrategyMapProps): JSX.Element {
  const [selectedObjId, setSelectedObjId] = useState<string | undefined>(undefined);

  const positions = useMemo(
    () => computeLayout(rootObjectives, childrenByParent),
    [rootObjectives, childrenByParent],
  );

  const connectors = useMemo(
    () => buildConnectors(positions, childrenByParent),
    [positions, childrenByParent],
  );

  const canvasW = useMemo(() => {
    if (positions.size === 0) return 600;
    return Math.max(...[...positions.values()].map((p) => p.x)) + CARD_W + CANVAS_PAD * 2;
  }, [positions]);

  const canvasH = useMemo(() => {
    if (positions.size === 0) return 400;
    return Math.max(...[...positions.values()].map((p) => p.y)) + CARD_H + CANVAS_PAD * 2;
  }, [positions]);

  const selectedObjective = selectedObjId ? objectivesById.get(selectedObjId) : undefined;
  const selectedKrs = selectedObjId ? (keyResultsByObjective.get(selectedObjId) ?? []) : [];

  const nodeList: NodePos[] = useMemo(
    () => [...positions.entries()].map(([id, p]) => ({ id, x: p.x, y: p.y })),
    [positions],
  );

  if (rootObjectives.length === 0) {
    return (
      <div className="flex h-full items-center justify-center text-sm text-[color:var(--app-text-muted)]">
        No objectives to display on the map.
      </div>
    );
  }

  return (
    <div className="flex h-full overflow-hidden">
      {/* Scrollable dotted canvas */}
      <div
        className="min-h-0 flex-1 overflow-auto"
        style={{
          backgroundImage: 'radial-gradient(circle, rgba(95, 107, 102, 0.28) 1px, transparent 1px)',
          backgroundSize: '18px 18px',
          backgroundColor: 'var(--app-panel-muted)',
        }}
      >
        <div
          className="relative"
          style={{
            width: `${canvasW}px`,
            height: `${canvasH}px`,
            minWidth: '100%',
            minHeight: '100%',
          }}
        >
          {/* SVG connector lines */}
          <svg
            className="pointer-events-none absolute inset-0"
            width={canvasW}
            height={canvasH}
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
          >
            <defs>
              <marker
                id="map-arrow"
                viewBox="0 0 10 10"
                refX="8"
                refY="5"
                markerWidth="5"
                markerHeight="5"
                orient="auto-start-reverse"
              >
                <path d="M 0 0 L 10 5 L 0 10 z" fill="rgb(148 163 184)" />
              </marker>
            </defs>
            {connectors.map(({ path, key }) => (
              <path
                key={key}
                d={path}
                stroke="rgb(148 163 184)"
                strokeWidth="1.5"
                strokeDasharray="5 4"
                fill="none"
                markerEnd="url(#map-arrow)"
              />
            ))}
          </svg>

          {/* Objective cards */}
          {nodeList.map(({ id, x, y }) => {
            const obj = objectivesById.get(id);
            if (!obj) return null;
            const progress = getObjectiveProgress(id);
            const krs = keyResultsByObjective.get(id) ?? [];
            const isSelected = selectedObjId === id;

            return (
              <button
                key={id}
                type="button"
                className={`absolute cursor-pointer rounded-lg border border-[color:var(--app-border)] bg-white/80 p-3 shadow-sm transition hover:bg-white ${
                  isSelected ? statusRingClass[obj.status] : ''
                }`}
                style={{ left: `${x}px`, top: `${y}px`, width: `${CARD_W}px`, textAlign: 'left' }}
                onClick={() => setSelectedObjId(isSelected ? undefined : id)}
              >
                {/* Card header */}
                <div className="mb-2 flex items-start gap-2">
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-xs font-semibold text-[color:var(--app-text)]">
                      {obj.title}
                    </p>
                    {obj.description && (
                      <p className="mt-0.5 line-clamp-1 text-[10px] text-[color:var(--app-text-soft)]">
                        {obj.description}
                      </p>
                    )}
                  </div>
                  <StatusBadge status={obj.status} />
                </div>

                {/* Progress */}
                <div className="mb-2 flex items-center gap-2">
                  <div className="flex-1">
                    <ProgressBar
                      value={progress}
                      colorClass={
                        obj.status === 'on_track'
                          ? 'bg-[color:var(--app-success)]'
                          : obj.status === 'at_risk'
                            ? 'bg-[color:var(--app-warning)]'
                            : 'bg-[color:var(--app-danger)]'
                      }
                    />
                  </div>
                  <span className="shrink-0 font-mono text-[10px] font-semibold text-[color:var(--app-text-muted)]">
                    {Math.round(progress)}%
                  </span>
                </div>

                {/* KR mini list */}
                {krs.length > 0 && (
                  <ul className="space-y-1 border-t border-[color:var(--app-border)] pt-2">
                    {krs.slice(0, 3).map((kr, i) => {
                      const krPct = getKrProgress(kr.id);
                      return (
                        <li key={kr.id} className="flex items-center gap-1.5">
                          <span className="shrink-0 font-mono text-[9px] text-[color:var(--app-text-soft)]">
                            K{i + 1}
                          </span>
                          <span className="flex-1 truncate text-[10px] text-[color:var(--app-text-muted)]">
                            {kr.title}
                          </span>
                          <span className="shrink-0 font-mono text-[9px] text-[color:var(--app-text-soft)]">
                            {Math.round(krPct)}%
                          </span>
                        </li>
                      );
                    })}
                    {krs.length > 3 && (
                      <li className="text-[10px] text-[color:var(--app-text-soft)]">
                        +{krs.length - 3} more KRs
                      </li>
                    )}
                  </ul>
                )}

                {/* Dates */}
                {(obj.startDate || obj.endDate) && (
                  <p className="mt-2 border-t border-[color:var(--app-border)] pt-2 text-[9px] text-[color:var(--app-text-soft)]">
                    {obj.startDate
                      ? new Date(obj.startDate).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                        })
                      : '—'}{' '}
                    →{' '}
                    {obj.endDate
                      ? new Date(obj.endDate).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                        })
                      : '—'}
                  </p>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Right inspector panel (shown when objective is selected) */}
      {selectedObjective && (
        <div className="w-72 shrink-0 overflow-auto border-l border-[color:var(--app-border)] bg-[color:var(--app-panel)] p-4">
          <div className="mb-4 flex items-start gap-2">
            <div className="min-w-0 flex-1">
              <p className={labelClass}>Selected objective</p>
              <h3 className="mt-1 text-sm font-semibold text-[color:var(--app-text)]">
                {selectedObjective.title}
              </h3>
            </div>
            <button
              type="button"
              onClick={() => setSelectedObjId(undefined)}
              className="ui-button ui-button-subtle h-8 w-8 shrink-0 px-0"
            >
              <X aria-hidden="true" size={14} />
            </button>
          </div>

          {/* Status + progress */}
          <div className="mb-4 space-y-3">
            <StatusBadge status={selectedObjective.status} />
            <div className="flex items-center gap-2">
              <div className="flex-1">
                <ProgressBar
                  value={getObjectiveProgress(selectedObjective.id)}
                  colorClass={
                    selectedObjective.status === 'on_track'
                      ? 'bg-[color:var(--app-success)]'
                      : selectedObjective.status === 'at_risk'
                        ? 'bg-[color:var(--app-warning)]'
                        : 'bg-[color:var(--app-danger)]'
                  }
                />
              </div>
              <span className="shrink-0 font-mono text-xs font-semibold text-[color:var(--app-text-muted)]">
                {Math.round(getObjectiveProgress(selectedObjective.id))}%
              </span>
            </div>
          </div>

          {/* Description */}
          {selectedObjective.description && (
            <div className={`${innerCardClass} mb-4 p-3`}>
              <p className={labelClass}>Description</p>
              <p className="mt-1 text-xs text-[color:var(--app-text-muted)]">
                {selectedObjective.description}
              </p>
            </div>
          )}

          {/* Dates */}
          {(selectedObjective.startDate || selectedObjective.endDate) && (
            <div className={`${innerCardClass} mb-4 p-3`}>
              <p className={labelClass}>Timeline</p>
              <p className="mt-1 text-xs text-[color:var(--app-text-muted)]">
                {selectedObjective.startDate
                  ? new Date(selectedObjective.startDate).toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric',
                    })
                  : '—'}{' '}
                →{' '}
                {selectedObjective.endDate
                  ? new Date(selectedObjective.endDate).toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric',
                    })
                  : '—'}
              </p>
            </div>
          )}

          {/* Key results */}
          {selectedKrs.length > 0 && (
            <div className={`${innerCardClass} p-3`}>
              <p className={labelClass}>Key results · {selectedKrs.length}</p>
              <ul className="mt-2 space-y-2">
                {selectedKrs.map((kr) => {
                  const krPct = getKrProgress(kr.id);
                  return (
                    <li
                      key={kr.id}
                      className="space-y-1 rounded-lg border border-[color:var(--app-border)] bg-white/70 p-2"
                    >
                      <p className="text-xs font-medium text-[color:var(--app-text)] line-clamp-2">
                        {kr.title}
                      </p>
                      <div className="flex items-center gap-2">
                        <div className="flex-1">
                          <ProgressBar value={krPct} colorClass="bg-[color:var(--app-info)]" />
                        </div>
                        <span className="shrink-0 font-mono text-[10px] text-[color:var(--app-text-soft)]">
                          {Math.round(krPct)}%
                        </span>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
